<template>
  <div class="mx-table">
    <Spin size="large" fix v-if="isConfiguring"></Spin>
    <t-table
      ref="table"
      :key="tableKey"
      v-bind="tableProps"
      v-on="$listeners"
      :columns="columnsHeader"
      :height="height || tableHeight"
      :border="border"
      :loading="loading"
      size="small"
    >
      <template
        v-for="(_, slotName) in $scopedSlots"
        v-slot:[slotName]="slotData"
      >
        <slot :name="slotName" v-bind="slotData || {}"></slot>
      </template>
    </t-table>
    <div class="pages">
      <Page
        :total="total"
        :current="current"
        :page-size="pageSize"
        :page-size-opts="[20, 30, 40]"
        placement="top"
        show-sizer
        :transfer="true"
        :show-total="showTotal"
        @on-change="pageChange"
        @on-page-size-change="sizeChange"
      >
      </Page>
    </div>
  </div>
</template>

<script>
export default {
  inheritAttrs: false,
  components: {},
  props: {
    columns: {},
    data: Array,
    total: Number,
    loading: Boolean,
    current: Number,
    pageSize: Number,
    height: [Number, String],
    'no-data-text': String,
    stripe: Boolean,
    isWarn: Boolean,
    draggable: Boolean,
    border: {
      type: Boolean,
      default: false,
    },
    showTotal: {
      type: Boolean,
      default: true,
    },
    rowClassName: Function,
    expandedRow: Function,
    expandIcon: {
      type: Boolean,
      default: false,
    },
    rowKey: {
      type: String,
      default: 'id',
    },
    expandedRowKeys: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    tableProps() {
      return {
        ...this.$attrs,
        ...this.$props,
      }
    },
  },
  data() {
    return {
      tableHeight: 0,
      columnsHeader: [],
      tableKey: 0,
      isConfiguring: false,
    }
  },
  mounted() {
    this.tableHeight = this.$refs.table.$el.offsetHeight
    window.addEventListener('resize', this.flexHeight)
    this.$eventBus.on('mx-column-setting-saved', this.initColumnsHeader)
    this.initColumnsHeader()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.flexHeight)
    this.$eventBus.off('mx-column-setting-saved', this.initColumnsHeader)
  },
  watch: {
    columns: {
      handler() {
        this.initColumnsHeader()
      },
      deep: true,
    },
  },
  methods: {
    initColumnsHeader(payload = {}) {
      if (payload && payload.path && payload.path !== this.$route.path) return
      const columnsList = Array.isArray(this.columns)
        ? Array.from(this.columns)
        : []
      let path = this.$route.path
      let data = null
      //获取缓存数据
      let localStorageColumn = localStorage.getItem('columnJson')
      if (localStorageColumn) {
        try {
          data = JSON.parse(localStorageColumn)[path]
        } catch (error) {
          data = null
        }
      }
      var list = []
      if (data && data.length > 0) {
        const columnMap = new Map()
        for (let i = 0; i < columnsList.length; i++) {
          const col = columnsList[i]
          if (col.title) columnMap.set(col.title, col)
        }

        const hasChecked = data.some((x) => x.check)
        for (let i = 0; i < data.length; i++) {
          const item = data[i]
          const col = columnMap.get(item.title)
          if (col) {
            if (!hasChecked || item.fixed || item.check) {
              list.push(col)
            }
          }
        }
      } else {
        list = columnsList
      }
      const newColumnsHeader = this.normalizeColumnsHeader(
        columnsList,
        data,
        list
      )

      // 优化卡顿：避免在 columns 内部深层数据改变时频繁销毁重建 table
      const getColKey = (c) =>
        `${c.key || c.colKey || c.type || c.title || ''}-${c.width || ''}-${
          c.fixed || ''
        }`
      const oldKeys = this.columnsHeader.map(getColKey).join('|')
      const newKeys = newColumnsHeader.map(getColKey).join('|')

      if (oldKeys !== newKeys) {
        this.isConfiguring = true
        setTimeout(() => {
          this.columnsHeader = newColumnsHeader
          this.tableKey += 1
          this.$nextTick(() => {
            setTimeout(() => {
              this.isConfiguring = false
            }, 500)
          })
        }, 1000)
      } else {
        this.columnsHeader = newColumnsHeader
      }
    },
    normalizeColumnsHeader(columnsList, savedColumns, resolvedColumns) {
      if (!Array.isArray(savedColumns) || savedColumns.length === 0) {
        return resolvedColumns
      }
      const hasChecked = savedColumns.some((item) => item.check)
      if (!hasChecked) return resolvedColumns

      const hasNormalColumns = columnsList.some((item) => !item.fixed)
      const resolvedNormalCount = resolvedColumns.filter(
        (item) => !item.fixed
      ).length
      if (hasNormalColumns && resolvedNormalCount === 0) {
        return columnsList
      }
      return resolvedColumns.length ? resolvedColumns : columnsList
    },
    flexHeight() {
      this.tableHeight = 0
      this.$nextTick(() => {
        if (this.$refs.table && this.$refs.table.$el) {
          this.tableHeight = this.$refs.table.$el.offsetHeight
        }
      })
    },
    pageChange(pageChanged) {
      this.$emit('pageChange', pageChanged)
    },
    sizeChange(sizeChanged) {
      this.$emit('sizeChange', sizeChanged)
    },
    selectAll(status) {
      this.$refs.table.selectAll(status)
    },
  },
}
</script>

<style lang="less" scoped>
.mx-table {
  width: 100%;
  display: flex;
  flex-flow: column;
  flex: auto;
  background: #fff;
  padding: 8px 20px 20px;
  border-radius: 0 0 6px 6px;
  position: relative;
  /deep/.t-table {
    flex: 1;
    color: #272e3b;
  }
  /deep/.t-table th {
    background-color: #f2f3f5;
    color: #272e3b;
    font-size: 14px;
    font-family: AlibabaPuHuiTi, sans-serif;
    border-bottom: 1px solid #e0e2e5;
  }
  /deep/.t-table td {
    padding: 9.5px 8px;
    color: #272e3b;
    font-size: 14px;
    font-family: AlibabaPuHuiTi, sans-serif;
    border-bottom: 1px solid #e0e2e5;
  }
  /deep/.pages {
    margin-top: 15px;
    text-align: right;
  }
  /deep/.ivu-page-item {
    min-width: 32px;
    height: 32px;
    line-height: 30px;
  }
  /deep/.ivu-page-item-active {
    border-color: rgba(20, 86, 240, 1);
    background-color: rgba(20, 86, 240, 1);
    a {
      color: #fff;
    }
  }
  /deep/.ivu-page-prev,
  /deep/.ivu-page-next,
  /deep/.ivu-page-item-jump-prev,
  /deep/.ivu-page-item-jump-next {
    min-width: 32px;
    height: 32px;
    line-height: 30px;
  }

  /deep/.ivu-page-options {
    .ivu-select-selection {
      height: 32px;
    }

    .ivu-select-selected-value {
      min-width: 32px;
      height: 32px;
      line-height: 30px;
    }
  }
  /deep/.ivu-btn-small {
    height: 26px;
  }
}
</style>

